import type { ConfiguratorPreviewContext } from '../preview-context';
import { resolveToken, TokenResolutionError } from './resolveToken';

const FALLBACK_ASSET_BASE = 'admin.configento.app';

/** What the admin answers for a stored upload. */
export interface StoredUpload {
    id: string;
    folder: string;
    type: string;
    file: string;
    url: string;
}

/** Upload failed — `status` is the HTTP status, 0 for a network error. */
export class UploadError extends Error {
    constructor(public readonly status: number, message: string) {
        super(message);
        this.name = 'UploadError';
    }
}

/**
 * POST one file of a file-upload characteristic to the admin's
 * `/api/v1/storefront/assets/files`. Same token handling as the other
 * asset writes: resolve the token up front (storefront plugins only hand
 * out a token endpoint), and on a 401 refresh it once and retry.
 */
export async function uploadFile(
    context: ConfiguratorPreviewContext,
    file: File,
    filename: string,
): Promise<StoredUpload> {
    const endpoint = `${baseUrl(context.assetBaseUrl)}/api/v1/storefront/assets/files`;

    let token = await token_(context, false);
    let res = await post(endpoint, file, filename, context.componentId, token);
    if (res.status === 401) {
        token = await token_(context, true);
        res = await post(endpoint, file, filename, context.componentId, token);
    }
    if (!res.ok) {
        throw new UploadError(res.status, `upload failed: HTTP ${res.status}`);
    }

    const json: unknown = await res.json().catch(() => null);
    if (!isStoredUpload(json)) {
        throw new UploadError(res.status, 'upload answered without a file reference');
    }
    return json;
}

async function token_(context: ConfiguratorPreviewContext, forceRefresh: boolean): Promise<string> {
    try {
        return await resolveToken(context, { forceRefresh });
    } catch (err) {
        if (err instanceof TokenResolutionError) {
            throw new UploadError(err.status, `token resolve failed: HTTP ${err.status}`);
        }
        throw err;
    }
}

async function post(endpoint: string, file: File, filename: string, componentId: string, token: string): Promise<Response> {
    const body = new FormData();
    body.append('file', file, filename);
    body.append('component_id', componentId);
    try {
        // No content-type header: the browser sets the multipart boundary.
        return await fetch(endpoint, {
            method: 'POST',
            headers: { accept: 'application/json', 'x-jscloud-tok': token },
            body,
        });
    } catch (err) {
        throw new UploadError(0, (err as Error).message);
    }
}

function isStoredUpload(x: unknown): x is StoredUpload {
    if (x === null || typeof x !== 'object') return false;
    const o = x as Record<string, unknown>;
    return typeof o.id === 'string' && typeof o.file === 'string' && typeof o.url === 'string';
}

function baseUrl(assetBaseUrl: string | undefined): string {
    const base = (assetBaseUrl ?? FALLBACK_ASSET_BASE).replace(/\/+$/, '');
    return base.startsWith('http') ? base : `https://${base}`;
}
